import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "../../components/datatable/datatable";
import SearchComponent from "../../components/search";
import BtwStatusPill from "../../components/btw-status-pill";
import { customerFetchPartnerBtwApplications } from "../../core/axios/endpoints";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");

const INVOICE_STATUS_LABELS = {
  pending: { label: "Nog niet gefactureerd", color: "bg-slate-100 text-slate-700" },
  invoiced: { label: "Gefactureerd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Betaald", color: "bg-emerald-100 text-emerald-800" },
  credited: { label: "Gecrediteerd", color: "bg-rose-100 text-rose-800" },
};

const PartnerBtwTeruggavesPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState({ data: [], totalDataCount: 0 });
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterWorkflowId, setFilterWorkflowId] = useState("");
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState("");

  const statusMap = useMemo(() => {
    const m = {};
    statuses.forEach((s) => { m[String(s._id)] = s; });
    return m;
  }, [statuses]);

  const fetchData = useCallback(
    async ({ page, pageSize, resetPageSize }, search = searchText) => {
      setLoading(true);
      try {
        const body = await customerFetchPartnerBtwApplications({
          page,
          limit: pageSize,
          search,
        });
        setStatuses(body?.statuses ?? []);
        setData({
          data: (body?.docs ?? []).map((item, index) => ({ ...item, id: index })),
          totalDataCount: body?.pagination?.totalDocs ?? 0,
          resetPageSize,
        });
      } catch (e) {
        console.error("Partner BTW lijst ophalen mislukt:", e);
      } finally {
        setLoading(false);
      }
    },
    [searchText],
  );

  useEffect(() => {
    fetchData({ page: 1, pageSize: 25, resetPageSize: false });
  }, []);

  const handleSearch = (q) => {
    setSearchText(q);
    fetchData({ page: 1, pageSize: 25, resetPageSize: true }, q);
  };

  const columns = [
    {
      title: "Naam",
      dataIndex: "step1",
      render: (step1) => {
        const naam = `${step1?.voorletters ?? ""} ${step1?.tussenvoegsel ?? ""} ${step1?.achternaam ?? ""}`
          .replace(/\s+/g, " ")
          .trim();
        return <span className="font-medium text-gray-900">{naam || "-"}</span>;
      },
    },
    {
      title: "Plaats",
      dataIndex: "step1",
      render: (step1) => (
        <span className="text-sm text-slate-600">
          {step1?.postcode ?? ""} {step1?.plaatsnaam ?? ""}
        </span>
      ),
    },
    {
      title: "Aanvraag",
      dataIndex: "createdAt",
      render: (v) => <span className="text-sm">{fmtDate(v)}</span>,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Installatie",
      dataIndex: "step3",
      render: (step3) => <span className="text-sm">{fmtDate(step3?.installationDate)}</span>,
    },
    {
      title: "Status",
      dataIndex: "workflowStatusId",
      render: (id) => {
        const status = id ? statusMap[String(id)] : null;
        return <BtwStatusPill status={status} size="sm" />;
      },
    },
    {
      title: "Factuur",
      dataIndex: "partnerInvoice",
      render: (pi) => {
        const cfg = INVOICE_STATUS_LABELS[pi?.status ?? "pending"];
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.color}`}>
            {cfg.label}
          </span>
        );
      },
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="p-4">
          <div className="mb-4">
            <h1 className="font-display text-2xl text-gray-900">BTW Teruggaves</h1>
            <p className="mt-1 text-sm text-slate-600">
              Overzicht van alle BTW-teruggave aanvragen die via uw bedrijf zijn binnengekomen.
            </p>
          </div>
          <SearchComponent onSearch={handleSearch} type={"partner-btw"} />

          {/* Filters */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <select
              value={filterWorkflowId}
              onChange={(e) => setFilterWorkflowId(e.target.value)}
              className="rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
            >
              <option value="">Alle workflow-statussen</option>
              {statuses.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
            <select
              value={filterInvoiceStatus}
              onChange={(e) => setFilterInvoiceStatus(e.target.value)}
              className="rounded-md border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
            >
              <option value="">Alle factuur-statussen</option>
              {Object.entries(INVOICE_STATUS_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
            {(filterWorkflowId || filterInvoiceStatus) ? (
              <button
                type="button"
                onClick={() => {
                  setFilterWorkflowId("");
                  setFilterInvoiceStatus("");
                }}
                className="rounded-md border border-gray-200 px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-50"
              >
                Wis filters
              </button>
            ) : null}
            <span className="ml-auto text-xs text-gray-500">
              {(() => {
                const filtered = (data?.data ?? []).filter((row) => {
                  if (filterWorkflowId && String(row.workflowStatusId?._id ?? row.workflowStatusId) !== filterWorkflowId) return false;
                  if (filterInvoiceStatus && (row.partnerInvoice?.status ?? "pending") !== filterInvoiceStatus) return false;
                  return true;
                });
                return `${filtered.length} van ${data?.data?.length ?? 0}`;
              })()}
            </span>
          </div>
        </div>
        <div className="table-responsive">
          <Table
            columns={columns}
            response={{
              ...data,
              data: (data?.data ?? []).filter((row) => {
                if (filterWorkflowId && String(row.workflowStatusId?._id ?? row.workflowStatusId) !== filterWorkflowId) return false;
                if (filterInvoiceStatus && (row.partnerInvoice?.status ?? "pending") !== filterInvoiceStatus) return false;
                return true;
              }),
            }}
            fetchData={fetchData}
            loading={loading}
            onRowClick={(record) => {
              if (record?._id) {
                navigate(`/panel/customer/btw-teruggaves/${record._id}`);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PartnerBtwTeruggavesPage;
